import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, Crown, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRICING, PLAN_LABELS, ADVISOR_MAILTO, fmtUsd } from "@/lib/plans";
import BillingToggle, { PriceLine } from "@/components/BillingToggle";

import { PLAN_COPY } from "@/lib/planCopy";

const COPY = Object.fromEntries(PLAN_COPY.map((p) => [p.key, p]));

function CurrentPlan({ info, isAdmin }) {
  const exp = info?.expires_at ? new Date(info.expires_at).toLocaleDateString() : null;
  return (
    <div className="card-flat p-6 flex flex-wrap items-center justify-between gap-4" data-testid="current-plan-card">
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 bg-brand-soft text-brand grid place-items-center rounded-sm"><Crown className="w-5 h-5" /></div>
        <div>
          <div className="stat-label">Your plan</div>
          <div className="font-display text-xl font-bold mt-1" data-testid="current-plan-label">{info?.label || PLAN_LABELS.explorer}</div>
        </div>
      </div>
      <div className="text-sm text-ink-muted text-right" data-testid="current-plan-meta">
        {isAdmin && <div className="text-brand font-medium">Admin · full access to all modules</div>}
        {!isAdmin && info?.expired && <div className="text-warn" data-testid="plan-ended-note">{info.trial ? "Your 14-day free trial has ended" : `Your ${PLAN_LABELS[info.plan]} access has ended`} — choose a plan to unlock your modules again.</div>}
        {!isAdmin && !info?.expired && info?.trial && exp && <div>Free trial · ends <b className="text-ink">{exp}</b> · no credit card needed</div>}
        {!isAdmin && !info?.expired && !info?.trial && exp && <div>{info.cycle === "annual" ? "Annual" : "Monthly"} · active until <b className="text-ink">{exp}</b></div>}
        {!isAdmin && info?.active_tender_limit ? <div className="text-[12px] mt-1" data-testid="plan-tender-limit">Up to {info.active_tender_limit} active tenders</div> : null}
      </div>
    </div>
  );
}

function PlanCard({ plan, cycle, highlight, isAdmin }) {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (isAdmin) { setQ({ error: "Included with your admin access" }); return; }
    setQ(null);
    api.get("/billing/quote", { params: { plan, cycle } })
      .then(({ data }) => setQ(data))
      .catch((ex) => setQ({ error: formatApiErrorDetail(ex.response?.data?.detail) }));
  }, [plan, cycle, isAdmin]);

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan, cycle, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      setBusy(false);
    }
  };

  const featured = plan === "professional";
  const label = !q ? "Loading…" : q.error ? q.error
    : q.kind === "upgrade" ? `Upgrade for ${fmtUsd(q.amount)}`
    : q.kind === "renew" ? `Renew · ${fmtUsd(q.amount)}` : `Buy ${PLAN_LABELS[plan].replace("TWIN ", "")} · ${fmtUsd(q.amount)}`;

  return (
    <div className={`p-7 rounded-sm flex flex-col ${featured ? "bg-brand text-white" : "bg-white border border-ink-line"} ${highlight ? "ring-2 ring-warn ring-offset-2" : ""}`} data-testid={`plan-card-${plan}`}>
      {featured && <div className="self-start bg-warn text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm mb-3" data-testid="plan-recommended-badge">Recommended</div>}
      <div className={`text-[11px] uppercase tracking-[0.2em] ${featured ? "text-white/70" : "text-ink-muted"}`}>{PLAN_LABELS[plan]}</div>
      <PriceLine plan={plan} cycle={cycle} pricing={PRICING} featured={featured} />
      <div className={`font-display font-semibold mt-4 ${featured ? "text-white" : "text-ink"}`}>{COPY[plan].headline}</div>
      <ul className="mt-5 space-y-2.5 text-sm flex-1">
        {COPY[plan].feats.map((f) => (
          <li key={f} className="flex gap-2"><CheckCircle2 className={`w-4 h-4 mt-0.5 ${featured ? "text-warn" : "text-good"}`} />{f}</li>
        ))}
      </ul>
      {COPY[plan].footnote && <p className={`text-[11px] mt-3 ${featured ? "text-white/60" : "text-ink-muted"}`} data-testid={`plan-footnote-${plan}`}>{COPY[plan].footnote}</p>}
      {q?.kind === "upgrade" && (
        <p className={`text-[12px] mt-4 ${featured ? "text-white/80" : "text-ink-muted"}`} data-testid="upgrade-note">
          You pay only the difference for the time left on your current {q.cycle} {PLAN_LABELS[q.from_plan]} plan. Your end date ({new Date(q.keeps_expiry).toLocaleDateString()}) and {q.cycle} cycle stay the same.
        </p>
      )}
      <button onClick={buy} disabled={busy || !q || !!q.error} data-testid={`plan-buy-${plan}`}
        className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-sm transition-colors disabled:opacity-60 ${featured ? "bg-white text-brand hover:bg-warn hover:text-white" : "bg-brand text-white hover:bg-brand-hover"}`}>
        <CreditCard className="w-4 h-4" /> {busy ? "Redirecting to Stripe…" : label}
      </button>
    </div>
  );
}

export default function Plans() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [cycle, setCycle] = useState(params.get("cycle") === "annual" ? "annual" : "monthly");
  const wanted = params.get("plan");

  return (
    <div className="space-y-8" data-testid="plans-page">
      <div className="border-b border-ink-line pb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Billing</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Plans & Billing</h1>
          <p className="text-sm text-ink-muted mt-1.5">Prices in USD. Secure payment by Stripe.</p>
        </div>
        <BillingToggle cycle={cycle} onChange={setCycle} />
      </div>
      <CurrentPlan info={user?.plan_info} isAdmin={user?.role === "admin"} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <PlanCard plan="explorer" cycle={cycle} highlight={wanted === "explorer"} isAdmin={user?.role === "admin"} />
        <PlanCard plan="growth" cycle={cycle} highlight={wanted === "growth"} isAdmin={user?.role === "admin"} />
        <PlanCard plan="professional" cycle={cycle} highlight={wanted === "professional"} isAdmin={user?.role === "admin"} />
        <div className="p-7 rounded-sm bg-white border border-ink-line flex flex-col" data-testid="plan-card-enterprise">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">{PLAN_LABELS.enterprise}</div>
          <div className="font-display text-2xl font-bold mt-4">Custom pricing</div>
          <p className="text-sm text-ink-muted mt-3 flex-1">Everything in Professional, plus custom features and multiple team users for your organisation.</p>
          <a href={ADVISOR_MAILTO} data-testid="plan-enterprise-contact" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-sm border border-ink-line hover:bg-secondary transition-colors">
            <MessageSquare className="w-4 h-4" /> Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
}
